import {
	BreakpointRange,
	ReactionsInBpRange,
	StateReactionData,
	EffectsReactionData,
} from '@wix/thunderbolt-becky-types'
import _ from 'lodash'

export type ReactionWithCondition = {
	triggerBpId: string
	reactions: Array<StateReactionData | EffectsReactionData>
}

export type BreakpointsData = { breakpointsRanges: { [id: string]: BreakpointRange }; breakpointsOrder: Array<string> }

const minusInfinity = -1
const plusInfinity = 1000000000

const orMinusInfinity = (value?: number): number => value || minusInfinity
const orPlusInfinity = (value?: number): number => value || plusInfinity

const subtractBreakpointRange = (minuend: BreakpointRange, subtrahend: BreakpointRange): Array<BreakpointRange> => {
	const intersectionEmpty =
		orMinusInfinity(minuend.min) > orPlusInfinity(subtrahend.max) ||
		orPlusInfinity(minuend.max) < orMinusInfinity(subtrahend.min)
	if (intersectionEmpty) {
		return [minuend]
	}

	const intersectionEqualsMinuend =
		orMinusInfinity(subtrahend.min) <= orMinusInfinity(minuend.min) &&
		orPlusInfinity(subtrahend.max) >= orPlusInfinity(minuend.max)
	if (intersectionEqualsMinuend) {
		return []
	}

	const shouldSplit =
		orMinusInfinity(subtrahend.min) > orMinusInfinity(minuend.min) &&
		orPlusInfinity(subtrahend.max) < orPlusInfinity(minuend.max)

	const shouldTrimLeft = !shouldSplit && orPlusInfinity(subtrahend.max) < orPlusInfinity(minuend.max)

	if (shouldSplit) {
		return [
			{ ...minuend, max: subtrahend.min! - 1 },
			{ ...minuend, min: subtrahend.max! + 1 },
		]
	}

	if (shouldTrimLeft) {
		return [{ ...minuend, min: subtrahend.max! + 1 }]
	}

	// trim right
	return [{ ...minuend, max: subtrahend.min! - 1 }]
}

const subtractManyBreakpointRanges = (
	minuend: BreakpointRange,
	subtrahends: Array<BreakpointRange>
): Array<BreakpointRange> => {
	let result = [minuend]

	subtrahends.forEach((nextSubtrahend) => {
		let nextResult: Array<BreakpointRange> = []
		result.forEach((nextMinuend) => {
			nextResult = [...nextResult, ...subtractBreakpointRange(nextMinuend, nextSubtrahend)]
		})
		result = nextResult
	})

	return result
}

const resolveOverlappingRanges = (cascadingRanges: Array<BreakpointRange>): Array<BreakpointRange> => {
	let resolvedRanges: Array<BreakpointRange> = []

	const addRange = (range: BreakpointRange) => {
		resolvedRanges = [...resolvedRanges, ...subtractManyBreakpointRanges(range, resolvedRanges)]
	}

	for (let i = cascadingRanges.length - 1; i >= 0; i--) {
		const range = cascadingRanges[i]
		addRange(range)
	}
	return resolvedRanges
}

const defaultBreakpointRange: BreakpointRange = { min: undefined, max: undefined, id: '', type: 'BreakpointRange' }

const getReactionsBreakpointsRanges = (
	reactionsInBp: Array<ReactionWithCondition>,
	{ breakpointsRanges, breakpointsOrder }: BreakpointsData
): Array<BreakpointRange> => {
	const bpIdsInUse = reactionsInBp.reduce<{ [bpId: string]: true }>((acc, { triggerBpId }) => {
		acc[triggerBpId] = true
		return acc
	}, {})

	const reactionsBPsIds = ['', ...breakpointsOrder].filter((bpId) => bpIdsInUse[bpId])
	const breakpointsRangesWithDefault: { [bpId: string]: BreakpointRange } = {
		...breakpointsRanges,
		'': defaultBreakpointRange,
	}

	return reactionsBPsIds.map((bpId) => breakpointsRangesWithDefault[bpId])
}

const toNonCascadingRanges = (
	reactionsInBp: Array<ReactionWithCondition>,
	breakpointsData: BreakpointsData
): Array<ReactionsInBpRange> => {
	const resolvedRanges = resolveOverlappingRanges(getReactionsBreakpointsRanges(reactionsInBp, breakpointsData))

	const result: Array<ReactionsInBpRange> = _.flatten(
		resolvedRanges.map<Array<ReactionsInBpRange>>((range) => {
			const reactions: Array<ReactionsInBpRange> = []
			reactionsInBp.forEach((reaction) => {
				if (reaction.triggerBpId === range.id) {
					reactions.push({
						triggerBpRange: {
							min: range.min,
							max: range.max,
						},
						reactions: reaction?.reactions || [],
					})
				}
			})
			return reactions
		})
	)

	result.sort((a, b) => orMinusInfinity(a.triggerBpRange.min) - orMinusInfinity(b.triggerBpRange.min))
	return result
}

export { toNonCascadingRanges, subtractBreakpointRange }
